import { useState } from 'react';
import whiteLabelService from '../../services/whiteLabelService';
import './scss/DomainManagement.scss';


const DomainManagement = ({ config, onUpdate, loading }) => {
  const [domain, setDomain] = useState(config?.domain || {});
  const [customDomain, setCustomDomain] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);



  const [errors, setErrors] = useState({});

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(type);

      setTimeout(() => {
        setCopiedKey(null);
      }, 2000); // show checkmark for 2 seconds
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };


  const handleSetDomain = async () => {
    const validation = whiteLabelService.validateDomain(customDomain);
    if (!validation.valid) {
      setErrors({ domain: validation.error });
      return;
    }

    try {
      const response = await whiteLabelService.setCustomDomain(customDomain);
      setDomain(response.domain || customDomain);
      setErrors({});

      // Update parent component with the full response including verification token
      await onUpdate({
        domain: {
          ...domain,
          customDomain: customDomain,
          domainVerified: false,
          verificationToken: response.verificationToken
        }
      });
    } catch (error) {
      setErrors({ domain: error.message || 'Failed to set domain' });
    }
  };

  const handleVerifyDomain = async () => {
    try {
      setVerifying(true);
      setErrors({}); // Clear previous errors
      const response = await whiteLabelService.verifyDomain();

      if (response.verified) {
        const updatedDomain = {
          ...config?.domain,
          domainVerified: true
        };
        setDomain(updatedDomain);
        await onUpdate({ domain: updatedDomain }); // Refresh parent state with updated domain

        // Show success message
        setErrors({ verification: null });
      } else {
        // Show detailed error message from backend
        const errorMessage = response.message || 'Domain verification failed. Please check your DNS configuration.';
        setErrors({ verification: errorMessage });

        // If backend provides instructions, we could show them
        if (response.instructions) {
          console.log('Verification instructions:', response.instructions);
        }
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to verify domain. Please try again.';
      setErrors({ verification: errorMessage });
    } finally {
      setVerifying(false);
    }
  };

  const currentDomain = config?.domain?.customDomain;
  const isVerified = config?.domain?.domainVerified;
  // Use subdomain if available, fallback to slug for backward compatibility
  const subdomainToUse = config?.subdomain || config?.slug || 'your-subdomain';
  const subdomainUrl = `https://${subdomainToUse}.zoobbe.com`;

  return (
    <div className="domain-management">
      <div className="section">
        <h3>Current Access URLs</h3>

        <div className="url-display">
          <div className="url-item">
            <label>
              Global Subdomain (Always Available)
              <span className="info-badge">✓ Auto-Generated</span>
            </label>
            <div className="url-value">
              <span>{subdomainUrl}</span>
              <button
                onClick={() => window.open(subdomainUrl, '_blank')}
                className="btn-link"
                title="Open in new tab"
              >
                <span className="material-symbols-outlined">open_in_new</span>
              </button>
            </div>
            <small className="url-description">
              This subdomain is automatically generated and always accessible, even when using a custom domain.
            </small>
          </div>

          {currentDomain && (
            <div className="url-item">
              <label>
                Custom Domain
                {isVerified ? (
                  <span className="status verified">✓ Verified</span>
                ) : (
                  <span className="status pending">⏳ Pending Verification</span>
                )}
              </label>
              <div className="url-value">
                <span>https://{currentDomain}</span>
                {isVerified && (
                  <button
                    onClick={() => window.open(`https://${currentDomain}`, '_blank')}
                    className="btn-link"
                  >
                    <span className="material-symbols-outlined">open_in_new</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="section">
        <h3>Custom Domain Setup</h3>

        {!currentDomain ? (
          <div className="domain-setup">
            <div className="form-group">
              <label>Custom Domain</label>
              <div className="domain-input">
                <input
                  type="text"
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                  placeholder="yourdomain.com"
                  className={errors.domain ? 'error' : ''}
                />
                <button
                  onClick={handleSetDomain}
                  disabled={loading || !customDomain}
                  className="btn btn-primary"
                >
                  Set Domain
                </button>
              </div>
              {errors?.domain && <span className="error-text">{errors.domain}</span>}
              <small>Enter your custom domain (e.g., yourdomain.com)</small>
            </div>
          </div>
        ) : (
          <div className="domain-configured">
            <div className="domain-info">
              <h4>Domain: {currentDomain}</h4>
              <p>Status: {isVerified ? 'Verified and Active' : 'Pending Verification'}</p>
            </div>

            {!isVerified && (
              <div className="verification-section">
                <h4>DNS Configuration Required</h4>
                <p>Please add the following DNS records to your domain:</p>

                <div className="dns-records">
                  <div className="dns-record">
                    <label>CNAME Record (Points your domain to Zoobbe)</label>
                    <div className="record-value">
                      <div className="record-item">
                        <span className="record-label">Name:</span>
                        <span className="record-text">{currentDomain}</span>
                        <button
                          onClick={() => copyToClipboard(currentDomain, 'CNAME Name')}
                          className="copy-btn"
                          title="Copy to clipboard"
                        >
                          <span className="material-symbols-outlined">
                            {copiedKey === 'CNAME Name' ? 'check' : 'content_copy'}
                          </span>
                        </button>
                      </div>
                      <div className="record-item">
                        <span className="record-label">Value:</span>
                        <span className="record-text">{config?.subdomain || config?.slug || 'your-slug'}.zoobbe.com</span>
                        <button
                          onClick={() => copyToClipboard(`${config?.subdomain || config?.slug || 'your-slug'}.zoobbe.com`, 'CNAME Value')}
                          className="copy-btn"
                          title="Copy to clipboard"
                        >
                          <span className="material-symbols-outlined">
                            {copiedKey === 'CNAME Value' ? 'check' : 'content_copy'}
                          </span>
                        </button>
                      </div>
                    </div>
                    <small className="dns-help">
                      This record routes traffic from your domain to Zoobbe's servers.
                    </small>
                  </div>

                  <div className="dns-record">
                    <label>TXT Record (for verification)</label>
                    <div className="record-value">
                      <div className="record-item">
                        <span className="record-label">Name:</span>
                        <span className="record-text">_acme-challenge.{currentDomain}</span>
                        <button
                          onClick={() => copyToClipboard(`_acme-challenge.${currentDomain}`, 'TXT Name')}
                          className="copy-btn"
                          title="Copy to clipboard"
                        >
                          <span className="material-symbols-outlined">
                            {copiedKey === 'TXT Name' ? 'check' : 'content_copy'}
                          </span>
                        </button>
                      </div>
                      <div className="record-item">
                        <span className="record-label">Value:</span>
                        <span className="record-text">{config?.domain?.verificationToken || 'Loading...'}</span>
                        <button
                          onClick={() => copyToClipboard(config?.domain?.verificationToken || '', 'TXT Value')}
                          className="copy-btn"
                          title="Copy to clipboard"
                          disabled={!config?.domain?.verificationToken}
                        >
                          <span className="material-symbols-outlined">
                            {copiedKey === 'TXT Value' ? 'check' : 'content_copy'}
                          </span>
                        </button>
                      </div>
                    </div>
                    <small className="dns-help">
                      Add this TXT record to your domain's DNS settings. For subdomains, you may need to add the record to your root domain instead.
                    </small>
                  </div>
                </div>

                <div className="verification-actions">
                  <button
                    onClick={handleVerifyDomain}
                    disabled={verifying}
                    className="btn btn-primary"
                  >
                    {verifying ? 'Verifying...' : 'Verify Domain'}
                  </button>

                  {errors.verification && (
                    <span className="error-text">{errors.verification}</span>
                  )}
                </div>
              </div>
            )}

            <div className="domain-actions">
              <button
                onClick={() => {
                  setCustomDomain('');
                  onUpdate({ domain: { customDomain: null, domainVerified: false } });
                }}
                className="btn btn-secondary"
              >
                Remove Custom Domain
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="section">
        <h3>Domain Help</h3>
        <div className="help-content">
          <div className="help-item">
            <h4>How to set up your custom domain:</h4>
            <ol>
              <li>Enter your desired domain above (e.g., app.yourcompany.com)</li>
              <li>Add both DNS records (CNAME and TXT) to your domain provider</li>
              <li>Wait for DNS propagation (can take up to 48 hours)</li>
              <li>Click "Verify Domain" to confirm setup</li>
              <li>Your white label instance will be accessible at your custom domain</li>
            </ol>
          </div>

          <div className="help-item">
            <h4>Important Notes:</h4>
            <ul>
              <li><strong>TXT Record Name:</strong> Must be exactly <code>_acme-challenge.yourdomain.com</code></li>
              <li><strong>Subdomains:</strong> For subdomains, you may need to add the TXT record to your root domain</li>
              <li><strong>DNS Propagation:</strong> Changes can take 5 minutes to 48 hours to take effect</li>
              <li><strong>Case Sensitive:</strong> Copy the verification token exactly as shown</li>
            </ul>
          </div>

          <div className="help-item">
            <h4>Common DNS Providers:</h4>
            <ul>
              <li><strong>Cloudflare:</strong> DNS → Records → Add Record</li>
              <li><strong>GoDaddy:</strong> DNS Management → Add Record</li>
              <li><strong>Namecheap:</strong> Advanced DNS → Add New Record</li>
              <li><strong>AWS Route 53:</strong> Hosted Zones → Create Record</li>
              <li><strong>Google Domains:</strong> DNS → Custom Records</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainManagement;
